<ng-container *transloco="let t; read: 'devices.assignContent'">
  <app-wizard-modal
    #wizard
    [(open)]="open"
    [title]="t('title')"
    [subtitle]="t('subtitle', { clientId: device.clientId })"
    [finishLabel]="t('assignToDevice')"
    [heightPx]="700"
    [busy]="isApplying()"
    (finished)="onFinished(wizard)"
    (closed)="onClose()"
  >
    <!-- 1 · Setup & profile -->
    <app-wizard-step
      id="setup"
      [label]="t('steps.setup')"
      [sublabel]="setupSublabel()"
      [description]="t('steps.setupDesc')"
      icon="icon-menu-settings1"
      [canLeave]="setupCanLeave"
    >
      <ng-template>
        @if (setupStatus() === 'loading') {
          <div class="flex items-center gap-3 rounded-control bg-sunken px-4 py-3.5">
            <app-spinner [size]="16" class="text-ink-3" />
            <span class="text-[13px] text-ink-2">{{ t('setup.loading') }}</span>
          </div>
        } @else {
          <div class="mb-4 flex flex-col gap-2 rounded-control bg-sunken p-3">
            <div class="flex items-center gap-3">
              <span
                class="flex h-5 w-5 flex-none items-center justify-center rounded-pill"
                [class.bg-success-surface]="displayWidth > 0"
                [class.text-success]="displayWidth > 0"
                [class.bg-danger-surface]="displayWidth === 0"
                [class.text-danger]="displayWidth === 0"
              >
                <app-icon [name]="displayWidth > 0 ? 'icon-checkmark' : 'icon-x'" [size]="12" />
              </span>
              <span class="flex-1 text-[13px] text-ink-1">{{ t('setup.deviceConfig') }}</span>
              <span class="text-[13px] text-ink-3">
                @if (displayWidth > 0) { {{ displayWidth }} × {{ displayHeight }} px }
              </span>
            </div>
            <div class="flex items-center gap-3">
              <span
                class="flex h-5 w-5 flex-none items-center justify-center rounded-pill"
                [class.bg-success-surface]="setupStatus() === 'ok'"
                [class.text-success]="setupStatus() === 'ok'"
                [class.bg-danger-surface]="setupStatus() !== 'ok'"
                [class.text-danger]="setupStatus() !== 'ok'"
              >
                <app-icon [name]="setupStatus() === 'ok' ? 'icon-checkmark' : 'icon-x'" [size]="12" />
              </span>
              <span class="flex-1 text-[13px] text-ink-1">{{ t('setup.operationProfiles') }}</span>
              <span class="text-[13px] text-ink-3">
                @if (setupStatus() === 'ok' || setupStatus() === 'noProfiles') {
                  {{ t('setup.profileCount', { count: availableProfiles().length }) }}
                }
              </span>
            </div>
          </div>

          @if (setupStatus() === 'noConfig') {
            <p class="mb-4 rounded-control bg-danger-surface px-4 py-3 text-[13px] font-semibold text-danger">
              {{ t('setup.noConfigError') }}
            </p>
          } @else if (setupStatus() === 'noProfiles') {
            <p class="mb-4 rounded-control bg-danger-surface px-4 py-3 text-[13px] font-semibold text-danger">
              {{ t('setup.noProfilesError') }}
            </p>
          } @else if (setupStatus() === 'error') {
            <p class="mb-4 rounded-control bg-danger-surface px-4 py-3 text-[13px] font-semibold text-danger">
              {{ t('setup.genericError') }}
            </p>
          } @else {
            <div role="radiogroup" [attr.aria-label]="t('setup.profilePicker')" class="flex flex-col gap-2">
              @for (profile of availableProfiles(); track profile.id) {
                <button
                  type="button"
                  role="radio"
                  [attr.aria-checked]="selectedProfileId() === profile.id"
                  (click)="selectedProfileId.set(profile.id)"
                  class="flex items-center gap-3 rounded-control p-3.5 text-left shadow-hairline"
                  [class.ring-2]="selectedProfileId() === profile.id"
                  [class.ring-action]="selectedProfileId() === profile.id"
                >
                  <span
                    class="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-pill border"
                    [class.border-action]="selectedProfileId() === profile.id"
                    [class.border-hairline]="selectedProfileId() !== profile.id"
                  >
                    @if (selectedProfileId() === profile.id) {
                      <span class="h-2.5 w-2.5 rounded-pill bg-action"></span>
                    }
                  </span>
                  <span class="min-w-0">
                    <span class="block text-[14px] font-semibold leading-5 text-ink-1">{{ profile.operationProfileId }}</span>
                    <span class="block text-[12px] leading-4 text-ink-3">{{ profile.powerMode }}</span>
                  </span>
                </button>
              }
            </div>
          }
        }
      </ng-template>
    </app-wizard-step>

    <!-- 2 · Activation -->
    <app-wizard-step
      id="events"
      [label]="t('steps.events')"
      [sublabel]="eventsSublabel()"
      icon="icon-menu-alerts1"
      [canLeave]="eventsCanLeave"
      [error]="eventsError()"
    >
      <ng-template>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
          @for (event of activationEvents; track event) {
            @let on = isEventOn(event);
            @let count = layoutsForEvent(event).length;
            <div
              class="rounded-control p-3.5"
              [class.bg-card]="on"
              [class.ring-2]="on"
              [class.ring-action]="on"
              [class.bg-sunken]="!on"
              [class.shadow-hairline]="!on"
            >
              <button
                type="button"
                (click)="toggleEvent(event, !on)"
                class="flex w-full items-start gap-3 text-left"
              >
                <span
                  class="flex h-[18px] w-[18px] flex-none items-center justify-center rounded-[4px]"
                  [class.bg-ink-1]="on"
                  [class.ring-1]="!on"
                  [class.ring-black/25]="!on"
                  [class.bg-white]="!on"
                >
                  @if (on) {
                    <app-icon name="icon-checkmark" [size]="12" class="text-white" />
                  }
                </span>
                <span class="min-w-0 flex-1">
                  <span class="flex items-center justify-between">
                    <span class="block text-[14px] font-semibold leading-5 text-ink-1">{{ t('events.' + event + '.label') }}</span>
                    @if (count > 1) {
                      <span class="rounded-pill bg-sunken px-2 py-0.5 text-[11px] font-semibold text-ink-2">
                        {{ t('events.layoutCount', { count }) }}
                      </span>
                    }
                  </span>
                  <span class="block text-[12px] leading-[18px] text-ink-3">{{ t('events.' + event + '.hint') }}</span>
                </span>
              </button>

              @if (event === 'BUTTON_EVENT' && on) {
                <div class="ml-[28px] mt-2">
                  <div class="flex gap-2">
                    @for (button of activationButtonOptions; track button) {
                      @let claimed = buttonLayout(button) !== null;
                      <button
                        type="button"
                        (click)="toggleButtonTile(button)"
                        class="flex h-[34px] w-[34px] items-center justify-center rounded-control text-[13px] font-semibold"
                        [class.bg-ink-1]="claimed"
                        [class.text-white]="claimed"
                        [class.bg-card]="!claimed"
                        [class.shadow-hairline]="!claimed"
                        [class.text-ink-1]="!claimed"
                      >
                        {{ button }}
                      </button>
                    }
                  </div>
                  <p class="mt-1 text-[11px] text-ink-3">{{ t('events.onePerButton') }}</p>
                </div>
              }

              @if (event === 'OFFLINE_MODE' && on) {
                <div class="ml-[28px] mt-2 max-w-[200px]">
                  <app-number-input
                    [ngModel]="offlineLayout()?.activationOfflineDelay"
                    (ngModelChange)="setOfflineDelay($event)"
                    [label]="t('events.offlineDelay')"
                    [unit]="t('units.seconds')"
                    [min]="0"
                  />
                </div>
              }

              @if (event === 'DEFAULT' && on) {
                <div class="ml-[28px] mt-2">
                  <div class="mb-1 text-[12px] font-bold uppercase tracking-wide text-ink-2">{{ t('events.rotationLabel') }}</div>
                  <div class="inline-flex items-center gap-3 rounded-control bg-card px-1 py-1 shadow-hairline">
                    <button
                      type="button"
                      (click)="decrementDefault()"
                      [disabled]="defaultCount() <= 1"
                      class="flex h-7 w-7 items-center justify-center rounded-[6px] text-[13px] font-bold text-ink-1 hover:bg-sunken disabled:opacity-30"
                    >−</button>
                    <span class="w-4 text-center text-[13px] font-bold text-ink-1">{{ defaultCount() }}</span>
                    <button
                      type="button"
                      (click)="incrementDefault()"
                      class="flex h-7 w-7 items-center justify-center rounded-[6px] text-[13px] font-bold text-ink-1 hover:bg-sunken"
                    >+</button>
                  </div>
                  @if (defaultCount() > 1) {
                    <p class="mt-1.5 text-[12px] text-ink-3">{{ t('events.rotationHintMany', { count: defaultCount(), period: templateSlideshowChangePeriod() }) }}</p>
                    <div class="mt-1.5 max-w-[160px]">
                      <app-number-input
                        [ngModel]="templateSlideshowChangePeriod()"
                        (ngModelChange)="setSlideshowPeriod($event)"
                        [label]="t('events.rotationPeriod')"
                        [unit]="t('units.seconds')"
                        [min]="1"
                      />
                    </div>
                  } @else {
                    <p class="mt-1.5 text-[12px] text-ink-3">{{ t('events.rotationHintOne') }}</p>
                  }
                </div>
              }
            </div>
          }
        </div>

        <p class="mt-4 text-[13px] font-semibold text-ink-2">{{ eventsHint() }}</p>
      </ng-template>
    </app-wizard-step>

    <!-- 3 · Content -->
    <app-wizard-step
      id="content"
      [label]="t('steps.content')"
      [sublabel]="contentSublabel()"
      icon="icon-menu-layouts1"
      [canLeave]="contentCanLeave"
      [error]="contentError()"
    >
      <ng-template>
        @if (layouts().length === 0) {
          <p class="rounded-control bg-sunken px-4 py-6 text-center text-[13px] text-ink-3">
            {{ t('content.noLayouts') }}
            <button type="button" (click)="wizard.goTo('events')" class="font-semibold text-link hover:underline">{{ t('review.edit') }}</button>
          </p>
        } @else {
          <!-- Tab strip -->
          <nav class="mb-5 flex gap-4 overflow-x-auto border-b border-hairline">
            @for (layout of layouts(); track layout.id; let li = $index) {
              @let active = effectiveActiveLayoutId() === layout.id;
              <button
                type="button"
                (click)="selectTab(layout.id)"
                class="flex flex-none flex-col items-start gap-0.5 border-b-2 px-0.5 pb-2 pt-1 text-left"
                [class.border-ink-1]="active"
                [class.border-transparent]="!active"
              >
                <span class="flex items-center gap-1.5 text-[13px] leading-[18px]" [class.font-semibold]="active" [class.text-ink-1]="active" [class.text-ink-2]="!active">
                  {{ layoutTabLabel(layout, li) }}
                  @if (layout.components.length === 0) {
                    <span class="h-1.5 w-1.5 rounded-pill bg-danger"></span>
                  }
                </span>
                <span class="text-[11px] leading-[14px] text-ink-3">{{ layoutEventDetail(layout) }}</span>
              </button>
            }
          </nav>

          @if (activeLayout(); as layout) {
            <!-- Components -->
            <div class="mb-2 flex items-center justify-between">
              <span class="text-[12px] font-bold uppercase tracking-wide text-ink-2">{{ t('layout.components') }}</span>
              @if (!editingComponent()) {
                <button type="button" (click)="startAddComponent()" class="flex items-center gap-1 text-[13px] font-semibold text-link hover:underline">
                  <app-icon name="icon-content-add24px" [size]="14" />
                  {{ t('layout.addComponent') }}
                </button>
              }
            </div>

            <div class="mb-4 flex gap-4">
              <div class="w-[176px] flex-none">
                <div
                  class="relative w-full overflow-hidden rounded-[4px] bg-ink-1"
                  [style.aspect-ratio]="displayWidth + ' / ' + displayHeight"
                >
                  @for (c of layout.components; track c.id) {
                    <div
                      class="absolute flex items-center justify-center border border-white/45 bg-white/[.16] px-1 text-center text-[10px] text-white"
                      [style.left]="rectStyle(c).left"
                      [style.top]="rectStyle(c).top"
                      [style.width]="rectStyle(c).width"
                      [style.height]="rectStyle(c).height"
                    >
                      {{ t('componentType.' + c.contentType) }}
                    </div>
                  }
                  @if (editingComponent(); as ec) {
                    <div
                      class="absolute flex items-center justify-center border-[1.5px] border-brand bg-brand/35 px-1 text-center text-[10px] text-white"
                      [style.left]="rectStyle(ec).left"
                      [style.top]="rectStyle(ec).top"
                      [style.width]="rectStyle(ec).width"
                      [style.height]="rectStyle(ec).height"
                    >
                      {{ t('componentType.' + ec.contentType) }}
                    </div>
                  }
                </div>
                <p class="mt-1 text-center text-[11px] text-ink-3">{{ displayWidth }} × {{ displayHeight }} px</p>
              </div>

              <div class="flex-1">
                @if (layout.components.length === 0) {
                  <p class="rounded-control bg-sunken px-3 py-4 text-center text-[12px] text-ink-3">{{ t('layout.componentsEmpty') }}</p>
                } @else {
                  <ul class="flex flex-col gap-2">
                    @for (c of layout.components; track c.id) {
                      <li class="flex items-center gap-3 rounded-control bg-sunken px-3 py-2">
                        <span class="flex-none rounded-[4px] bg-card px-1.5 py-0.5 text-[11px] font-bold text-ink-1 shadow-hairline">
                          {{ componentFileCount(c) > 1 ? t('componentType.PLAYLIST') : t('componentType.' + c.contentType) }}
                        </span>
                        <span class="min-w-0 flex-1 truncate text-[13px] leading-[18px] text-ink-1">
                          {{ componentFileNames(c) }}
                        </span>
                        <span class="flex-none text-[11px] leading-4 text-ink-3">{{ c.left }}, {{ c.top }} → {{ c.right }}, {{ c.bottom }}</span>
                        <button type="button" (click)="removeComponent(layout, c)" class="flex-none text-ink-3 hover:text-danger">
                          <app-icon name="icon-x" [size]="16" />
                        </button>
                      </li>
                    }
                  </ul>
                }
              </div>
            </div>

            <!-- Inline "new component" panel -->
            @if (editingComponent(); as ec) {
              <div class="flex flex-col gap-4 rounded-control bg-sunken p-4">
                <div class="flex items-center justify-between">
                  <span class="text-[13px] font-semibold text-ink-1">{{ t('newComponent.title') }}</span>
                  <div class="inline-flex rounded-control bg-card p-0.5 shadow-hairline">
                    <button
                      type="button"
                      (click)="setComponentType('IMAGE')"
                      class="rounded-[6px] px-3 py-1 text-[12px] font-semibold"
                      [class.bg-sunken]="ec.contentType === 'IMAGE'"
                      [class.text-ink-1]="ec.contentType === 'IMAGE'"
                      [class.text-ink-3]="ec.contentType !== 'IMAGE'"
                    >{{ t('componentType.images') }}</button>
                    <button
                      type="button"
                      (click)="setComponentType('SVG')"
                      class="rounded-[6px] px-3 py-1 text-[12px] font-semibold"
                      [class.bg-sunken]="ec.contentType === 'SVG'"
                      [class.text-ink-1]="ec.contentType === 'SVG'"
                      [class.text-ink-3]="ec.contentType !== 'SVG'"
                    >{{ t('componentType.SVG') }}</button>
                  </div>
                </div>

                <!-- Position -->
                <div class="flex gap-4">
                  <div class="w-[150px] flex-none">
                    <div
                      class="relative w-full overflow-hidden rounded-[4px] bg-ink-1"
                      [style.aspect-ratio]="displayWidth + ' / ' + displayHeight"
                    >
                      <div
                        class="absolute border-[1.5px] border-brand bg-brand/35"
                        [style.left]="rectStyle(ec).left"
                        [style.top]="rectStyle(ec).top"
                        [style.width]="rectStyle(ec).width"
                        [style.height]="rectStyle(ec).height"
                      ></div>
                    </div>
                  </div>
                  <div class="flex-1">
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <app-number-input [(ngModel)]="ec.left" (ngModelChange)="clampComponentRect()" [label]="t('newComponent.left')" [min]="0" [max]="displayWidth" />
                      <app-number-input [(ngModel)]="ec.top" (ngModelChange)="clampComponentRect()" [label]="t('newComponent.top')" [min]="0" [max]="displayHeight" />
                      <app-number-input [(ngModel)]="ec.right" (ngModelChange)="clampComponentRect()" [label]="t('newComponent.right')" [min]="0" [max]="displayWidth" />
                      <app-number-input [(ngModel)]="ec.bottom" (ngModelChange)="clampComponentRect()" [label]="t('newComponent.bottom')" [min]="0" [max]="displayHeight" />
                    </div>
                    @if (!componentRectValid()) {
                      <p class="mt-1.5 text-[12px] font-semibold text-danger">{{ t('newComponent.invalidRect') }}</p>
                    }
                  </div>
                </div>

                <!-- Media library -->
                <div>
                  <nav class="mb-2 flex items-center gap-1 text-[13px] text-ink-2">
                    @for (crumb of breadcrumb(); track crumb.id; let i = $index; let last = $last) {
                      @if (!last) {
                        <button (click)="navigateTo(i)" class="text-link hover:underline">{{ crumb.name }}</button>
                        <span class="text-ink-3">/</span>
                      } @else {
                        <span class="font-semibold text-ink-1">{{ crumb.name }}</span>
                      }
                    }
                  </nav>

                  @if (isLoadingFiles()) {
                    <div class="flex h-24 items-center justify-center text-ink-3">
                      <app-spinner [size]="20" />
                    </div>
                  } @else if (items().length === 0) {
                    <p class="py-6 text-center text-[13px] text-ink-3">{{ t('newComponent.folderEmpty') }}</p>
                  } @else {
                    <div class="grid grid-cols-4 gap-2">
                      @for (item of items(); track item.id) {
                        <button
                          type="button"
                          (click)="onItemClick(item)"
                          [disabled]="item.type === 'FILE' && !isSelectable(item)"
                          class="relative flex flex-col gap-1 rounded-control bg-card p-2 text-left shadow-hairline"
                          [class.ring-2]="isPicked(item)"
                          [class.ring-action]="isPicked(item)"
                          [class.opacity-35]="item.type === 'FILE' && !isSelectable(item)"
                        >
                          @if (item.type === 'FOLDER') {
                            <div class="flex aspect-[4/3] items-center justify-center rounded-[4px] bg-sunken">
                              <app-icon name="icon-menu-medialibrary1" [size]="20" class="text-ink-3" />
                            </div>
                          } @else if (isImage(item)) {
                            <img [src]="getThumbnailUrl(item)" alt="" class="aspect-[4/3] w-full rounded-[4px] object-cover" />
                          } @else {
                            <div class="flex aspect-[4/3] items-center justify-center rounded-[4px] bg-surface-3 text-[10px] font-bold uppercase text-ink-3">
                              {{ item.name.split('.').pop() }}
                            </div>
                          }
                          <span class="truncate text-[11px] text-ink-1">{{ item.name }}</span>
                          @if (isPicked(item)) {
                            <span class="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-pill bg-ink-1 text-white">
                              <app-icon name="icon-checkmark" [size]="10" />
                            </span>
                          }
                        </button>
                      }
                    </div>
                    @if (totalPages() > 1) {
                      <div class="mt-2 flex items-center justify-between text-[12px] text-ink-2">
                        <button (click)="prevPage()" [disabled]="page() === 0" class="disabled:opacity-40">{{ t('newComponent.previous') }}</button>
                        <span>{{ page() + 1 }} / {{ totalPages() }}</span>
                        <button (click)="nextPage()" [disabled]="page() >= totalPages() - 1" class="disabled:opacity-40">{{ t('newComponent.next') }}</button>
                      </div>
                    }
                  }
                </div>

                <!-- Picked files / playlist -->
                @if (ec.files.length > 0) {
                  <div>
                    <div class="mb-1.5 text-[12px] font-bold uppercase tracking-wide text-ink-2">
                      {{ ec.files.length > 1 ? t('newComponent.playlistHeader', { count: ec.files.length }) : t('newComponent.fileHeader') }}
                    </div>
                    <ul class="flex flex-col gap-1.5">
                      @for (pf of ec.files; track pf.file.id; let i = $index) {
                        <li class="flex items-center gap-2 rounded-control bg-card px-2.5 py-2 shadow-hairline">
                          @if (ec.files.length > 1) {
                            <span class="flex-none text-[12px] font-semibold text-ink-3">{{ i + 1 }}</span>
                          }
                          <span class="min-w-0 flex-1 truncate text-[13px] text-ink-1">{{ pf.file.name }}</span>
                          @if (ec.contentType === 'IMAGE' && ec.files.length > 1) {
                            <app-number-input
                              class="w-24 flex-none"
                              [ngModel]="pf.duration"
                              (ngModelChange)="setPickedFileDuration(i, $event)"
                              [unit]="t('units.secondsShort')"
                              [min]="1"
                            />
                            <button type="button" (click)="movePickedFile(i, -1)" [disabled]="i === 0" class="flex-none text-ink-3 hover:text-ink-1 disabled:opacity-30">
                              <app-icon name="icon-arrow-collapse" [size]="16" />
                            </button>
                            <button type="button" (click)="movePickedFile(i, 1)" [disabled]="i === ec.files.length - 1" class="flex-none text-ink-3 hover:text-ink-1 disabled:opacity-30">
                              <app-icon name="icon-arrow-dropdown" [size]="16" />
                            </button>
                          }
                          <button type="button" (click)="removePickedFile(pf.file)" class="flex-none text-ink-3 hover:text-danger">
                            <app-icon name="icon-x" [size]="14" />
                          </button>
                        </li>
                      }
                    </ul>
                  </div>
                }

                <div class="flex justify-end gap-2">
                  <button type="button" (click)="cancelAddComponent()" class="rounded-control border border-hairline bg-card px-3 py-1.5 text-[12px] font-semibold text-ink-1 hover:bg-sunken">
                    {{ t('newComponent.cancel') }}
                  </button>
                  <button
                    type="button"
                    (click)="finishAddComponent()"
                    [disabled]="ec.files.length === 0 || !componentRectValid()"
                    class="rounded-control bg-action px-3 py-1.5 text-[12px] font-semibold text-action-text hover:brightness-90 disabled:opacity-40"
                  >
                    {{ t('newComponent.add') }}
                  </button>
                </div>
              </div>
            }
          }
        }
      </ng-template>
    </app-wizard-step>

    <!-- 4 · Review & assign -->
    <app-wizard-step
      id="review"
      [label]="t('steps.review')"
      [sublabel]="reviewSublabel()"
      icon="icon-checkmark"
    >
      <ng-template>
        @if (layouts().length === 0) {
          <p class="rounded-control bg-sunken px-4 py-6 text-center text-[13px] text-ink-3">{{ t('review.noLayouts') }}</p>
        } @else {
          <div class="flex flex-col gap-3">
            @for (layout of layouts(); track layout.id; let li = $index) {
              <div class="flex gap-3 rounded-control bg-card p-3 shadow-hairline">
                <div
                  class="relative w-[104px] flex-none overflow-hidden rounded-[4px] bg-ink-1"
                  [style.aspect-ratio]="displayWidth + ' / ' + displayHeight"
                >
                  @for (c of layout.components; track c.id) {
                    <div
                      class="absolute border border-white/45 bg-white/[.16]"
                      [style.left]="rectStyle(c).left"
                      [style.top]="rectStyle(c).top"
                      [style.width]="rectStyle(c).width"
                      [style.height]="rectStyle(c).height"
                    ></div>
                  }
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center justify-between">
                    <span class="text-[13px] font-semibold text-ink-1">{{ layoutTabLabel(layout, li) }} · {{ layoutEventDetail(layout) }}</span>
                    <button type="button" (click)="selectTab(layout.id); wizard.goTo('content')" class="text-[12px] text-link hover:underline">
                      {{ t('review.edit') }}
                    </button>
                  </div>
                  <p class="mt-1 text-[12px] text-ink-3">
                    {{ t('layout.componentCount', { count: layout.components.length }) }}
                    @if (layout.components.length > 0) { · {{ layoutComponentTypesLine(layout) }} }
                  </p>
                  @if (layout.activationEvent === 'OFFLINE_MODE') {
                    <p class="text-[12px] text-ink-3">{{ t('events.offlineDelay') }}: {{ layout.activationOfflineDelay }}s</p>
                  }
                  @if (layout.components.length === 0) {
                    <p class="mt-1 text-[12px] font-semibold text-danger">
                      {{ t('layout.componentsRequired') }}
                      <button type="button" (click)="selectTab(layout.id); wizard.goTo('content')" class="underline">{{ t('review.fixIt') }}</button>
                    </p>
                  }
                </div>
              </div>
            }
          </div>
        }
      </ng-template>
    </app-wizard-step>
  </app-wizard-modal>
</ng-container>
